import { useRef, useCallback, useState, useEffect, useId } from "react";
import { useSignUpMutation } from "services/api";
import { AuthPageCover } from "components/covers";
import {
	Card,
	alpha,
	Typography,
	Grid,
	Box,
	Button,
	Link,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	DialogContentText,
	Backdrop,
	CircularProgress,
	Stack,
} from "custom_exports/material_ui_items";
import SignUpForm from "./SignUpForm";
import OtpForm from "./OtpForm";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

function SignUpPage(props) {
	//--------------------------------- variables ---------------------------------//
	// ---- initializations ---- //

	// ---- assignments ---- //
	const formRef = useRef();
	const userDataRef = useRef(null);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const [signUpApiCaller, signUpApiResult] = useSignUpMutation();

	const [otp, setOtp] = useState(null);
	const [error, setError] = useState(location.state?.errorMessage || "");
	const [userAccountCreateConfirmation, setUserAccountCreateConfirmation] =
		useState("");

	const [isResendOtpDisabled, setIsResendOtpDisabled] = useState(false);
	const [timer, setTimer] = useState(0);
	const [rateLimitCooldown, setRateLimitCooldown] = useState(0);

	// Turnstile state
	const [turnstileToken, setTurnstileToken] = useState(null);
	const turnstileWidgetId = useRef(null);
	const turnstileContainerId = useId();

	// eslint-disable-next-line no-undef
	const SITE_KEY = TURNSTILE_SITE_KEY;

	//--------------------------------- functions ---------------------------------//
	// ---- handlers ---- //
	const handleForm = useCallback(
		({ type, data }) => {
			if (type === "submit") {
				if (!turnstileToken) {
					setError("Please complete the verification challenge");
					return;
				}
				signUpApiCaller({ ...data, password: "", turnstile_token: turnstileToken });
				userDataRef.current = data;
			}
			setOtp(null);
			setError("");
		},
		[signUpApiCaller, turnstileToken]
	);

	const verifyOtp = () => {
		setError("");
		if (otp && otp.every((data) => data)) {
			signUpApiCaller({
				...userDataRef.current,
				otp: otp.join(""),
			});
		}
	};

	const handleResendClick = () => {
		setError("");
		setIsResendOtpDisabled(true);
		setTimer(60);
		signUpApiCaller(userDataRef.current);
	};

	// ---- useEffects ---- //

	// Initialize Turnstile widget
	useEffect(() => {
		const initTurnstile = () => {
			if (window.turnstile && !turnstileWidgetId.current) {
				const container = document.getElementById(turnstileContainerId);
				if (container) {
					turnstileWidgetId.current = window.turnstile.render(container, {
						sitekey: SITE_KEY,
						callback: (token) => {
							setTurnstileToken(token);
						},
						"expired-callback": () => {
							setTurnstileToken(null);
						},
						"error-callback": () => {
							setTurnstileToken(null);
						},
						theme: "light",
					});
				}
			}
		};

		// Check if turnstile is already loaded
		if (window.turnstile) {
			initTurnstile();
		} else {
			// Wait for turnstile to load
			const checkInterval = setInterval(() => {
				if (window.turnstile) {
					clearInterval(checkInterval);
					initTurnstile();
				}
			}, 100);

			return () => clearInterval(checkInterval);
		}

		return () => {
			if (turnstileWidgetId.current && window.turnstile) {
				window.turnstile.remove(turnstileWidgetId.current);
				turnstileWidgetId.current = null;
			}
		};
	}, [turnstileContainerId, SITE_KEY]);

	// Reset Turnstile after failed attempts or successful submission
	const resetTurnstile = useCallback(() => {
		if (turnstileWidgetId.current && window.turnstile) {
			window.turnstile.reset(turnstileWidgetId.current);
			setTurnstileToken(null);
		}
	}, []);

	useEffect(() => {
		if (signUpApiResult.isSuccess) {
			// setOtp(null);
			// setError("");
			if (signUpApiResult?.data?.status === "fail") {
				if (signUpApiResult.data.data?.user_exists === true) {
					navigate("/auth/sign-in", {
						state: {
							errorMessage: signUpApiResult.data.message,
							email: userDataRef.current?.email || "",
						},
					});
				} else if (otp) {
					setError("Invalid OTP");
				}
				// if (signInApiResult?.data?.data?.user_inactive === true) {
				// 	setUserInactiveMessage(signInApiResult.data.message);
				// }
			} else {
				if (signUpApiResult.data.data.user_inactive === true) {
					setUserAccountCreateConfirmation(signUpApiResult.data.message);
				} else {
					// setOtp(["", "", "", "", "", ""]);
				}
			}
			signUpApiResult.reset();
		}
	}, [signUpApiResult, dispatch, otp, navigate]);

	useEffect(() => {
		let interval = null;

		if (isResendOtpDisabled && timer > 0) {
			// Start countdown
			interval = setInterval(() => {
				setTimer((prevTimer) => prevTimer - 1);
			}, 1000);
		} else if (timer === 0) {
			// Enable the link when the timer reaches 0
			setIsResendOtpDisabled(false);
			clearInterval(interval);
		}

		return () => clearInterval(interval);
	}, [isResendOtpDisabled, timer]);

	// Rate limit cooldown timer
	useEffect(() => {
		let interval = null;

		if (rateLimitCooldown > 0) {
			interval = setInterval(() => {
				setRateLimitCooldown((prev) => prev - 1);
			}, 1000);
		}

		return () => clearInterval(interval);
	}, [rateLimitCooldown]);

	// Handle API errors including rate limiting
	useEffect(() => {
		if (signUpApiResult.isError) {
			const errorStatus = signUpApiResult.error?.status;
			const errorData = signUpApiResult.error?.data;

			if (errorStatus === 429) {
				// Rate limit exceeded - use fixed 60 second cooldown (wait time not revealed by API)
				setRateLimitCooldown(60);
				setError(errorData?.message || "Too many attempts. Please try again later.");
			} else if (errorData?.message) {
				// Validation error from backend
				setError(errorData.message);
			} else {
				setError("An error occurred. Please try again.");
			}
			// Reset Turnstile on any error so user can retry
			resetTurnstile();
			signUpApiResult.reset();
		}
	}, [signUpApiResult, resetTurnstile]);

	//--------------------------------- elements ----------------------------------//

	const element = (
		<AuthPageCover>
			<Card
				sx={{
					position: "relative",
					zIndex: 0,
					boxShadow: `0 0 2px 0 ${alpha(
						"#919EAB",
						0.2
					)}, 0 12px 24px -4px ${alpha("#919EAB", 0.12)}`,
					overflow: "visible",
					py: 4,
					px: 4,
				}}
				style={{ borderRadius: 16 }}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					{otp ? (
						<Typography
							color="#000000"
							variant="caption"
							fontSize="17px"
							sx={{ textAlign: { xs: "center", md: "center" } }}
							fontWeight={"500"}
						>
							{
								"We’ve sent an OTP to your email. Please enter it below to verify your identity"
							}
						</Typography>
					) : (
						<>
							<Typography
								color="primary"
								variant="h5"
								gutterBottom
								sx={{ fontWeight: "600", color: "#000000" }}
								textAlign="center"
							>
								Sign Up
							</Typography>
							{error && !otp && (
								<Typography
									variant="h7"
									alignSelf={"center"}
									color="error"
									fontSize={"14px"}
								>
									{error}
								</Typography>
							)}
						</>
					)}
					{otp ? (
						<OtpForm otp={otp} setOtp={setOtp} />
					) : (
						<>
							<SignUpForm formRef={formRef} handleForm={handleForm} />
							{/* Turnstile widget container */}
							<Box
								id={turnstileContainerId}
								sx={{
									mt: 2,
									display: "flex",
									justifyContent: "center",
								}}
							/>
						</>
					)}
					<Stack
						direction={"row"}
						justifyContent={otp && error ? "space-between" : "flex-end"}
						width={"92%"}
						mt={3}
					>
						{error && otp && (
							<Typography
								variant="h7"
								alignSelf={"center"}
								color="error"
								fontSize={"15px"}
								fontWeight={"600"}
							>
								{error}
							</Typography>
						)}

						{otp && (
							<Typography alignSelf={"flex-end"}>
								<Link
									sx={{
										textDecoration: "none",
										color: isResendOtpDisabled
											? "grey"
											: "black",
										// my: 2,
										cursor: isResendOtpDisabled
											? "default"
											: "pointer",
									}}
									component="button"
									variant="body2"
									onClick={handleResendClick}
									disabled={isResendOtpDisabled}
								>
									{isResendOtpDisabled
										? `Resend OTP in ${timer} seconds`
										: "Resend OTP"}
								</Link>
							</Typography>
						)}
					</Stack>
					{otp ? (
						<Button
							disabled={
								signUpApiResult.isLoading ||
								(otp && otp.some((data) => !data))
							}
							fullWidth
							variant="contained"
							sx={{
								mt: 3,
								mb: 2,
								borderRadius: 18,
								// height: 40,
								fontSize: 16,
								fontWeight: 600,
								width: "50%",
								background: "#015bf8",
								color: "#fff",
								"&:hover": {
									bgcolor: "#000000",
								},
							}}
							onClick={verifyOtp}
						>
							{"Sign Up"}
						</Button>
					) : (
						<Button
							disabled={signUpApiResult.isLoading || rateLimitCooldown > 0 || !turnstileToken}
							fullWidth
							variant="contained"
							sx={{
								mt: 3,
								mb: 2,
								borderRadius: 18,
								// height: 40,
								fontSize: 16,
								fontWeight: 600,
								width: "50%",
								background: "#015bf8",
								color: "#fff",
								"&:hover": {
									bgcolor: "#000000",
								},
								"&:disabled": {
									bgcolor: "#cccccc",
								},
							}}
							onClick={() => {
								if (formRef.current) {
									formRef.current.submitForm(); // Trigger the submit form method
								}
							}}
						>
							{rateLimitCooldown > 0
								? `Wait ${rateLimitCooldown}s`
								: "Sign Up"}
						</Button>
					)}
					<Grid container justifyContent="center">
						<Grid item>
							<Stack mt={2} direction={"row"}>
								<Typography
									variant="body3"
									// sx={{ fontWeight: "600" }}
								>
									{"Existing User ? "}
								</Typography>
								<Link
									href="/auth/sign-in"
									variant="body1"
									sx={{
										fontWeight: "600",
										ml: 1,
										color: "#015bf8",
									}}
								>
									{"Sign In"}
								</Link>
							</Stack>
						</Grid>
					</Grid>
				</Box>
			</Card>

			{/*  */}
			<Backdrop
				sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={signUpApiResult.isLoading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>

			{/*  */}
			{/*  */}
			<Dialog
				open={!!userAccountCreateConfirmation}
				aria-labelledby="responsive-dialog-title"
				fullWidth
				// maxWidth=""
				sx={{
					"& .MuiPaper-root": {
						borderRadius: "16px", // Rounded corners for modern look
						padding: "12px", // Add padding to make it feel spacious
						backgroundColor: "#f5f5f5", // Light background for modern feel
					},
				}}
			>
				<DialogTitle
					id="responsive-dialog-title"
					sx={{
						textAlign: "center",
						fontWeight: "bold",
						fontSize: "1rem", // Larger text for title
						color: "#333", // Darker text for better contrast
					}}
				>
					Account Created Successfully
				</DialogTitle>
				<DialogContent>
					<DialogContentText textAlign={"justify"}>
						{userAccountCreateConfirmation}
					</DialogContentText>
				</DialogContent>

				<DialogActions
					sx={{ justifyContent: "center", paddingBottom: "16px" }}
				>
					<Button
						onClick={() => navigate("/")}
						variant="contained" // Use contained button for modern look
						color="primary" // Use error color for emphasis
						sx={{
							width: "100px",
							borderRadius: "8px", // Modern button with rounded corners
							textTransform: "none", // Remove uppercase text transformation
						}}
					>
						Ok
					</Button>
				</DialogActions>
			</Dialog>
		</AuthPageCover>
	);

	return element;
}

export default SignUpPage;
